// apps/web/src/components/BottomNavigation.tsx

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { trpc } from '@/trpc/client';
import { motion } from 'framer-motion';
import {
  Home,
  Package,
  PlusCircle,
  Truck,
  User,
  Briefcase,
  MapPin,
  Wallet,
  Settings,
  Activity,
  Newspaper,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
}

const BottomNavigation: React.FC = () => {
  const { user } = useAuthStore();
  const location = useLocation();

  // ALWAYS call the hook, but only enable it when needed
  // This fixes the "Rendered more hooks" error
  const { data: inProgressJobs } = trpc.booking.getMyActiveBookings?.useQuery(
    undefined,
    {
      enabled: !!user && user.role === 'TRANSPORTER', // Only fetch for transporters and only when user exists
      refetchInterval: !!user && user.role === 'TRANSPORTER' ? 60_000 : false, // Reduce polling to 60s
      staleTime: 25_000,
    }
  ) || { data: [] };

  if (!user) return null;

  // ═══════════════════════════════════════════════════════════════════════════
  // NAVIGATION ITEMS BY ROLE
  // ═══════════════════════════════════════════════════════════════════════════

  const clientNavItems: NavItem[] = [
    { label: 'Home', icon: Home, href: '/dashboard' },
    { label: 'Blog', icon: Newspaper, href: '/blog' },
    { label: 'Jobs', icon: Briefcase, href: '/jobs' },
    { label: 'Post', icon: PlusCircle, href: '/jobs/create' },
    { label: 'Profile', icon: User, href: '/dashboard/profile' },
  ];

  const transporterNavItems: NavItem[] = [
    {
      label: 'Active',
      icon: Home,
      href: '/dashboard/transporter',
      badge: inProgressJobs?.length || 0
    },
    { label: 'Blog', icon: Newspaper, href: '/blog' },
    { label: 'Jobs', icon: MapPin, href: '/jobs' },
    { label: 'Wallet', icon: Wallet, href: '/dashboard/wallet' },
    { label: 'Vehicles', icon: Truck, href: '/dashboard/my-vehicles' },
    { label: 'Profile', icon: User, href: '/dashboard/profile' },
  ];

  const adminNavItems: NavItem[] = [
    { label: 'Dashboard', icon: Home, href: '/admin/dashboard' },
    { label: 'Blog', icon: Newspaper, href: '/admin/blog' },
    { label: 'Users', icon: User, href: '/admin/users' },
    { label: 'Jobs', icon: Briefcase, href: '/admin/jobs' },
    { label: 'Vehicles', icon: Truck, href: '/admin/vehicles' },
    { label: 'Settings', icon: Settings, href: '/admin/audit-logs' },
  ];

  const fleetManagerNavItems: NavItem[] = [
    { label: 'Dashboard', icon: Home, href: '/dashboard/fleet' },
    { label: 'Blog', icon: Newspaper, href: '/blog' },
    { label: 'Jobs', icon: Briefcase, href: '/jobs' },
    { label: 'Wallet', icon: Wallet, href: '/dashboard/wallet' },
    { label: 'Vehicles', icon: Truck, href: '/dashboard/my-vehicles' },
    { label: 'Profile', icon: User, href: '/dashboard/profile' },
  ];

  // Select navigation items based on role
  const navItems =
    user.role === 'ADMIN'
      ? adminNavItems
      : user.role === 'FLEET_MANAGER'
        ? fleetManagerNavItems
        : user.role === 'TRANSPORTER'
          ? transporterNavItems
          : clientNavItems;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
      {/* Glassmorphism Container */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-lg border-t border-white/10 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.3)]" />

      <div className="relative flex items-center justify-around w-full h-auto py-2 pb-safe min-h-[4rem]">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          const showBadge = item.badge && item.badge > 0;

          return (
            <Link
              key={item.href}
              to={item.href}
              id={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                'flex flex-col items-center justify-center flex-1 relative transition-colors py-1',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground/60 hover:text-foreground'
              )}
            >
              {/* Active indicator (Top Glow) */}
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary shadow-[0_0_10px_var(--primary)] rounded-b-full"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}

              {/* Icon with scale animation */}
              <motion.div
                animate={{
                  scale: isActive ? 1.1 : 1,
                  y: isActive ? -2 : 0,
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="relative"
              >
                <Icon
                  className={cn(
                    'w-6 h-6',
                    isActive && 'drop-shadow-[0_0_8px_rgba(var(--primary),0.6)]'
                  )}
                />

                {/* Badge */}
                {showBadge && (
                  <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center shadow-lg border border-background">
                    {item.badge! > 9 ? '9+' : item.badge}
                  </span>
                )}
              </motion.div>

              {/* Label */}
              <span
                className={cn(
                  'text-[10px] mt-1 font-medium transition-all tracking-wide',
                  isActive ? 'opacity-100 text-foreground' : 'opacity-0 scale-75'
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;